import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { generateEmail } from "@/lib/jobbkartaApi";
import {
  Sparkles,
  Copy,
  Mail,
  RefreshCw,
  Loader2,
  UserRound,
  Wand2,
} from "lucide-react";

const TONES = [
  { id: "formal", label: "Professionell & formell" },
  { id: "personal", label: "Personlig & drivande" },
  { id: "concise", label: "Kort & koncis" },
];

export default function EmailGeneratorDialog({
  open,
  onOpenChange,
  company,
  profile,
  isProfileComplete,
  onEditProfile,
  onApplicationSent,
}) {
  const [tone, setTone] = useState("formal");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [recipient, setRecipient] = useState("");
  const [loading, setLoading] = useState(false);
  const [generated, setGenerated] = useState(false);

  useEffect(() => {
    if (open && company) {
      setSubject("");
      setBody("");
      setGenerated(false);
      setTone("formal");
      setRecipient(company.email || "");
    }
  }, [open, company]);

  if (!company) return null;

  const runGenerate = async () => {
    if (!isProfileComplete) {
      toast.error("Fyll i din profil först (namn och e-post).");
      onEditProfile();
      return;
    }
    setLoading(true);
    try {
      const data = await generateEmail({
        company: {
          name: company.name,
          industry: company.industry || "",
          city: company.city || "",
          email: company.email || "",
          website: company.website || "",
          description: company.description || "",
        },
        profile,
        tone,
      });
      setSubject(data.subject);
      setBody(data.body);
      setGenerated(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunde inte generera e-post. Försök igen.");
    } finally {
      setLoading(false);
    }
  };

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(`Ämne: ${subject}\n\n${body}`);
      toast.success("E-posttexten kopierad!");
    } catch {
      toast.error("Kunde inte kopiera.");
    }
  };

  const mailtoHref = `mailto:${encodeURIComponent(recipient)}?subject=${encodeURIComponent(
    subject
  )}&body=${encodeURIComponent(body)}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="jk-scroll max-h-[90vh] max-w-lg overflow-y-auto" data-testid="email-generator-dialog">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-100 text-emerald-700">
              <Wand2 className="h-5 w-5" />
            </span>
            <div className="min-w-0">
              <DialogTitle className="truncate text-left font-display text-lg">
                Spontanansökan till {company.name}
              </DialogTitle>
              <DialogDescription className="text-left text-xs">
                AI:n skriver ett förslag – du kan redigera allt själv innan du skickar.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4">
          {!isProfileComplete && (
            <button
              onClick={onEditProfile}
              data-testid="email-complete-profile-cta"
              className="flex w-full items-center gap-2 rounded-xl border border-dashed border-emerald-300 bg-emerald-50 p-3 text-left text-sm text-emerald-800"
            >
              <UserRound className="h-4 w-4" />
              Fyll i din profil så blir mailet skräddarsytt – klicka här.
            </button>
          )}

          <div>
            <Label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">
              Ton
            </Label>
            <div className="flex flex-wrap gap-2" data-testid="ai-modal-tone-selector">
              {TONES.map((t) => (
                <button
                  key={t.id}
                  data-testid={`tone-${t.id}`}
                  onClick={() => setTone(t.id)}
                  className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${
                    tone === t.id
                      ? "border-emerald-500 bg-emerald-600 text-white"
                      : "border-slate-300 bg-white text-slate-600 hover:border-emerald-300"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          <Button
            onClick={runGenerate}
            disabled={loading}
            data-testid="generate-email-btn"
            className="w-full gap-2 rounded-xl bg-emerald-600 hover:bg-emerald-700"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> AI:n skriver…
              </>
            ) : (
              <>
                {generated ? <RefreshCw className="h-4 w-4" /> : <Sparkles className="h-4 w-4" />}
                {generated ? "Skapa ny version" : "Skapa e-post med AI"}
              </>
            )}
          </Button>

          <div className="space-y-1.5">
            <Label htmlFor="em-recipient">Mottagare (e-post)</Label>
            <Input
              id="em-recipient"
              data-testid="email-recipient-input"
              value={recipient}
              onChange={(e) => setRecipient(e.target.value)}
              placeholder="fyll i om företaget saknar e-post"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="em-subject">Ämne</Label>
            <Input
              id="em-subject"
              data-testid="email-subject-input"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Ämnesrad…"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="em-body">Meddelande</Label>
            <Textarea
              id="em-body"
              data-testid="email-output-textarea"
              rows={12}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Skriv ditt mail här, eller låt AI:n skapa ett förslag ovan…"
              className="jk-scroll leading-relaxed"
            />
          </div>

          <div className="flex flex-col gap-2 sm:flex-row">
            <Button
              variant="outline"
              onClick={copyEmail}
              disabled={!body}
              data-testid="copy-email-btn"
              className="flex-1 gap-2 rounded-xl"
            >
              <Copy className="h-4 w-4" /> Kopiera text
            </Button>
            <Button
              asChild
              disabled={!body}
              data-testid="open-mailto-btn"
              className="flex-1 gap-2 rounded-xl bg-slate-900 hover:bg-slate-800"
            >
              <a
                href={body ? mailtoHref : "#"}
                onClick={() => {
                  if (body) onApplicationSent?.();
                }}
              >
                <Mail className="h-4 w-4" /> Öppna i e-postprogram
              </a>
            </Button>
          </div>
          <p className="text-center text-xs text-slate-400">
            Mailet skickas från din egen e-postadress, så företaget kan svara direkt till dig.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
